import React from 'react';

const FileUploadProgress = ({ files, progress, onUpload }) => {
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getProgressIcon = (fileName) => {
    const fileProgress = progress[fileName];
    if (!fileProgress) {
      return <i className="bi bi-clock text-muted"></i>;
    }
    
    switch (fileProgress.status) {
      case 'uploading':
        return <div className="spinner-border spinner-border-sm text-primary" role="status"></div>;
      case 'completed':
        return <i className="bi bi-check-circle-fill text-success"></i>;
      case 'error':
        return <i className="bi bi-x-circle-fill text-danger"></i>;
      default:
        return <i className="bi bi-clock text-muted"></i>;
    }
  };

  const getProgressText = (fileName) => {
    const fileProgress = progress[fileName];
    if (!fileProgress) {
      return 'Waiting to upload';
    }
    
    switch (fileProgress.status) {
      case 'uploading':
        return `Uploading... ${fileProgress.progress}%`;
      case 'completed':
        return 'Upload completed';
      case 'error':
        return fileProgress.error || 'Upload failed';
      default:
        return 'Waiting to upload';
    }
  };

  const getProgressBarClass = (fileName) => {
    const fileProgress = progress[fileName];
    if (!fileProgress) return 'bg-secondary';
    
    switch (fileProgress.status) {
      case 'uploading':
        return 'bg-primary progress-bar-striped progress-bar-animated';
      case 'completed':
        return 'bg-success';
      case 'error':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  };

  const getProgressValue = (fileName) => {
    const fileProgress = progress[fileName];
    return fileProgress ? fileProgress.progress : 0;
  };

  const hasAnyProgress = Object.keys(progress).length > 0;
  const allCompleted = files.length > 0 && files.every(file => progress[file.name]?.status === 'completed');
  const totalProgress = files.length > 0 
    ? Math.round(files.reduce((sum, file) => sum + getProgressValue(file.name), 0) / files.length)
    : 0;

  return (
    <div>
      {!hasAnyProgress ? (
        <div className="text-center py-4">
          <i className="bi bi-upload display-4 text-primary mb-3"></i>
          <h5>Ready to Upload</h5>
          <p className="text-muted mb-4">
            Your files have been scanned and are ready to be uploaded. Click the upload button to begin.
          </p>
          
          <div className="list-group">
            {files.map((file, index) => (
              <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <i className="bi bi-file-earmark me-3 text-primary"></i>
                  <div>
                    <div className="fw-medium">{file.name}</div>
                    <small className="text-muted">
                      {formatFileSize(file.size)} • {file.type || 'Unknown type'}
                    </small>
                  </div>
                </div>
                <span className="badge bg-primary">Ready</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div>
          {/* Overall Progress */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">
                <i className="bi bi-cloud-upload me-2"></i>
                Upload Progress
              </h6>
              <span className="badge bg-primary">{totalProgress}% Complete</span>
            </div>
            <div className="progress" style={{ height: '8px' }}>
              <div
                className={`progress-bar ${allCompleted ? 'bg-success' : 'bg-primary progress-bar-striped progress-bar-animated'}`}
                role="progressbar"
                style={{ width: `${totalProgress}%` }}
                aria-valuenow={totalProgress}
                aria-valuemin="0"
                aria-valuemax="100"
              ></div>
            </div>
          </div>

          {/* Individual File Progress */}
          <h6 className="mb-3">File Upload Status</h6>
          <div className="list-group">
            {files.map((file, index) => (
              <div key={index} className="list-group-item">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      {getProgressIcon(file.name)}
                    </div>
                    <div>
                      <div className="fw-medium">{file.name}</div>
                      <small className="text-muted">
                        {formatFileSize(file.size)}
                      </small>
                    </div>
                  </div>
                  <small className="text-muted">
                    {getProgressText(file.name)}
                  </small>
                </div>
                <div className="progress" style={{ height: '4px' }}>
                  <div
                    className={`progress-bar ${getProgressBarClass(file.name)}`}
                    role="progressbar"
                    style={{ width: `${getProgressValue(file.name)}%` }}
                    aria-valuenow={getProgressValue(file.name)}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  ></div>
                </div>
              </div>
            ))}
          </div>

          {allCompleted && (
            <div className="alert alert-success mt-3">
              <i className="bi bi-check-circle-fill me-2"></i>
              <strong>Upload Complete!</strong> All files have been successfully uploaded. It may take several minutes for the knowledge base to reflect the new files.
            </div>
          )}

          {Object.values(progress).some(p => p.status === 'error') && (
            <div className="alert alert-danger mt-3">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <strong>Upload Error:</strong> Some files failed to upload. Please try again.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FileUploadProgress;
